import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../App";
import { toast } from "sonner";

const RAFHA_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/rogqq55c_F1371478-432E-404F-80CC-D2C1450E0839.png";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ 
    name: "", 
    email: "", 
    password: "", 
    role: "customer" 
  });

  const from = location.state?.from || "/dashboard";

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Login failed";
      
      if (errorMessage.includes("Invalid credentials")) {
        toast.error("Invalid email or password. Please check and try again.");
      } else if (error.code === "ERR_NETWORK") {
        toast.error("Network error. Please check your internet connection.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Client-side password validation
    if (registerForm.password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(registerForm.email, registerForm.password, registerForm.name, registerForm.role);
      toast.success("Account created successfully!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Register error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Registration failed";
      
      // Provide helpful error messages
      if (errorMessage.includes("already registered")) {
        toast.error("This email is already registered. Try signing in instead.");
      } else if (errorMessage.includes("password") || errorMessage.includes("Password")) {
        toast.error("Password must be at least 8 characters. Letters and numbers work best.");
      } else if (error.code === "ERR_NETWORK") {
        toast.error("Connection error. Please check your internet and try again.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="relative w-28 h-28 mx-auto mb-6">
            <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-gold/10 to-transparent rounded-full"></div>
            <img 
              src={RAFHA_LOGO}
              alt="Rafha Entertainment"
              className="w-28 h-28 object-contain relative z-10 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
            />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Welcome
          </h1>
          <p className="text-white/60">
            Sign in to book entertainment or manage your vendor profile
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-charcoal rounded-2xl border border-white/5 p-6"
        >
          {/* Google Sign In */}
          <Button
            data-testid="google-signin-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-12 border-white/10 text-white hover:bg-white/5 rounded-full mb-6"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-charcoal px-4 text-white/40">or continue with email</span>
            </div>
          </div>
          
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="w-full bg-obsidian border border-white/5 rounded-full p-1 mb-6">
              <TabsTrigger 
                value="login" 
                className="flex-1 rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid="login-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="flex-1 rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid="register-tab"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-white/60">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="login-email"
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      placeholder="you@example.com"
                      className="pl-12 h-12 bg-obsidian border-white/10 text-white placeholder:text-white/30 rounded-full"
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="login-password" className="text-white/60">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      placeholder="••••••••"
                      className="pl-12 pr-12 h-12 bg-obsidian border-white/10 text-white placeholder:text-white/30 rounded-full"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gold text-black hover:bg-gold-light rounded-full font-bold gold-glow"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-white/60">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="register-name"
                      type="text"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      required
                      placeholder="John Doe"
                      className="pl-12 h-12 bg-obsidian border-white/10 text-white placeholder:text-white/30 rounded-full"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="register-email" className="text-white/60">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="register-email"
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      placeholder="you@example.com"
                      className="pl-12 h-12 bg-obsidian border-white/10 text-white placeholder:text-white/30 rounded-full"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="register-password" className="text-white/60">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Min 8 characters"
                      className="pl-12 pr-12 h-12 bg-obsidian border-white/10 text-white placeholder:text-white/30 rounded-full"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-white/60">I am a...</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, role: "customer" })}
                      className={`p-4 rounded-xl border transition-colors ${
                        registerForm.role === "customer"
                          ? "border-gold bg-gold/10 text-gold"
                          : "border-white/10 text-white/60 hover:border-white/20"
                      }`}
                      data-testid="role-customer-btn"
                    >
                      <User className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">Customer</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, role: "vendor" })}
                      className={`p-4 rounded-xl border transition-colors ${
                        registerForm.role === "vendor"
                          ? "border-gold bg-gold/10 text-gold"
                          : "border-white/10 text-white/60 hover:border-white/20"
                      }`}
                      data-testid="role-vendor-btn"
                    >
                      <Briefcase className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">Vendor</span>
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gold text-black hover:bg-gold-light rounded-full font-bold gold-glow"
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

// Add missing import
import { Briefcase } from "lucide-react";

export default AuthPage;
